import React, { useState, useEffect, useMemo, useRef } from 'react';
import { supabaseAdmin } from '../lib/supabaseClient';
import { Certification, CertificationDB } from '../types';
import { safeUrl } from '../constants';

const Certifications: React.FC = () => {
  const [certifications, setCertifications] = useState<Certification[]>([]);
  const [loading, setLoading] = useState(true);
  
  // States for Filters
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [yearFilter, setYearFilter] = useState('all');
  const [publisherFilter, setPublisherFilter] = useState('all');

  // Search State
  const [searchQuery, setSearchQuery] = useState('');
  const [searchHistory, setSearchHistory] = useState<string[]>([]);
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    // Load search history from localStorage
    try {
      const savedHistory = localStorage.getItem('certSearchHistory');
      if (savedHistory) {
        setSearchHistory(JSON.parse(savedHistory));
      }
    } catch (error) {
      console.error('Error loading search history:', error);
      setSearchHistory([]);
    }
  }, []);

  const handleSearch = (query: string) => {
    setSearchQuery(query);
  };

  const saveToHistory = (query: string) => {
    if (!query.trim()) return;
    
    const newHistory = [query, ...searchHistory.filter(h => h !== query)].slice(0, 5);
    setSearchHistory(newHistory);
    
    try {
      localStorage.setItem('certSearchHistory', JSON.stringify(newHistory));
    } catch (error) {
      console.error('Error saving search history:', error);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      saveToHistory(searchQuery);
      searchInputRef.current?.blur();
      setIsSearchFocused(false);
    }
  };

  const clearHistory = () => {
    setSearchHistory([]);

    try {
      localStorage.removeItem('certSearchHistory');
    } catch (error) {
      console.error('Error clearing search history:', error);
    }
  };

  // FETCH DATA
  useEffect(() => {
    const fetchCerts = async () => {
      setLoading(true);
      try {
        const { data, error } = await supabaseAdmin
          .from('certifications')
          .select('*')
          .order('sort_order', { ascending: true })
          .order('issued_date', { ascending: false });

        if (error) throw error;

        if (data) {
          // Fix #3: Type assertion using Interface
          const dbData = data as CertificationDB[];

          // Database (Snake_case) -> UI (CamelCase + Styling)
          const formattedData: Certification[] = dbData.map((item) => {
            // Fix #14: Robust Date Parsing
            const dateObj = new Date(item.issued_date);
            const isValidDate = !isNaN(dateObj.getTime());
            
            const yearStr = isValidDate ? dateObj.getFullYear().toString() : 'N/A';
            const monthStr = isValidDate ? dateObj.toLocaleString('default', { month: 'short' }) : '';
            
            // Determine hover styles and icons based on cert_type
            let hoverStyle = 'hover:border-blue-500 dark:hover:border-blue-500 hover:shadow-blue-500/20 dark:hover:shadow-blue-500/10'; // Default Professional
            let iconName = 'verified';

            if (item.cert_type === 'Course') {
              hoverStyle = 'hover:border-emerald-500 dark:hover:border-emerald-500 hover:shadow-emerald-500/20 dark:hover:shadow-emerald-500/10';
              iconName = 'school';
            } else if (item.cert_type === 'Project') {
              hoverStyle = 'hover:border-purple-500 dark:hover:border-purple-500 hover:shadow-purple-500/20 dark:hover:shadow-purple-500/10';
              iconName = 'architecture';
            } else if (item.cert_type === 'Workshop') {
              hoverStyle = 'hover:border-amber-500 dark:hover:border-amber-500 hover:shadow-amber-500/20 dark:hover:shadow-amber-500/10';
              iconName = 'lightbulb';
            }

            return {
              id: item.id.toString(),
              title: item.title,
              issuer: item.issuer,
              date: isValidDate ? `Issued ${monthStr} ${yearStr}` : 'Date Unknown',
              year: yearStr,
              type: item.cert_type as "Professional" | "Course" | "Project" | "Workshop",
              description: item.description,
              imageUrl: item.image_url,
              credentialUrl: safeUrl(item.credential_link),
              // Styling
              hoverColor: hoverStyle,
              icon: iconName,
              categoryColor: '' 
            };
          });
          
          setCertifications(formattedData);
        }
      } catch (error) {
        console.error('Error fetching certifications:', error);
        if (typeof window !== 'undefined') {
          // It's often better to show UI error state than alert, but keeping alert as requested
          // window.alert('Failed to load certifications.'); 
        }
      } finally {
        setLoading(false);
      }
    };

    fetchCerts();
  }, []);

  // Fix #5: Use useMemo to prevent recalculating filters on every render
  const years = useMemo(() => {
    const allYears = certifications.map(c => c.year);
    const validYears = allYears.filter(year => year && year !== 'N/A');
    return Array.from(new Set(validYears)).sort().reverse();
  }, [certifications]);

  const publishers = useMemo(() => {
    return Array.from(new Set(certifications.map(c => c.issuer))).sort();
  }, [certifications]);

  // Fix #5: Memoize the filtered list as well
  const filteredCerts = useMemo(() => {
    return certifications.filter(cert => {
      // Basic Filters
      const categoryMatch = categoryFilter === 'all' || categoryFilter.toLowerCase() === cert.type.toLowerCase();
      const yearMatch = yearFilter === 'all' || yearFilter === cert.year;
      const publisherMatch = publisherFilter === 'all' || cert.issuer === publisherFilter;

      // Search Logic (Real-time)
      const normalizedQuery = searchQuery.toLowerCase().trim();
      const searchMatch = !normalizedQuery || 
        cert.title.toLowerCase().includes(normalizedQuery) ||
        cert.issuer.toLowerCase().includes(normalizedQuery) ||
        cert.description.toLowerCase().includes(normalizedQuery);

      return categoryMatch && yearMatch && publisherMatch && searchMatch;
    });
  }, [certifications, categoryFilter, yearFilter, publisherFilter, searchQuery]);

  const professionalCerts = filteredCerts.filter(c => c.type === 'Professional');
  const courseCerts = filteredCerts.filter(c => c.type === 'Course');
  const projectCerts = filteredCerts.filter(c => c.type === 'Project');
  const workshopCerts = filteredCerts.filter(c => c.type === 'Workshop');

  return (
    <div className="w-full flex flex-col items-center animate-fade-in-up">
      <div className="w-full max-w-[960px] px-4 sm:px-6 lg:px-8 py-10 sm:py-16">
        <div className="flex flex-col gap-4 text-center items-center">
          <h1 className="text-slate-900 dark:text-white text-4xl sm:text-5xl font-black leading-tight tracking-tighter">
            Certifications
          </h1>
          <p className="text-slate-600 dark:text-gray-400 text-lg font-normal leading-relaxed max-w-2xl">
            Technology never stands still, and neither do I. A chronicle of my learning journey through bootcamps, courses, and professional exams.
          </p>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
        </div>
      ) : (
        <>
          <div className="w-full max-w-[960px] px-4 sm:px-6 lg:px-8 mb-8 z-40 space-y-4">
            {/* Advanced Search Bar */}
            <div className="relative">
              <div className={`relative flex items-center w-full h-14 rounded-xl bg-white dark:bg-card-dark border transition-all duration-300 ${isSearchFocused ? 'border-primary shadow-lg ring-1 ring-primary/20' : 'border-gray-200 dark:border-gray-700 hover:border-primary dark:hover:border-primary hover:shadow-sm'}`}>
                <span className="material-symbols-outlined absolute left-4 text-slate-400 text-2xl">search</span>
                <input
                  ref={searchInputRef}
                  type="text"
                  placeholder="Search by title, issuer, or keyword..."
                  className="w-full h-full pl-12 pr-12 bg-transparent border-none outline-none focus:outline-none focus:ring-0 text-slate-900 dark:text-white placeholder-slate-400 text-base"
                  value={searchQuery}
                  onChange={(e) => handleSearch(e.target.value)}
                  onFocus={() => setIsSearchFocused(true)}
                  onBlur={() => setTimeout(() => setIsSearchFocused(false), 200)} // Delay to allow chip click
                  onKeyDown={handleKeyDown}
                />
                {searchQuery && (
                  <button 
                    onClick={() => setSearchQuery('')}
                    className="absolute right-4 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors"
                  >
                    <span className="material-symbols-outlined text-xl">close</span>
                  </button>
                )}
              </div>

              {/* Search History Chips */}
              {isSearchFocused && searchHistory.length > 0 && (
                <div className={`absolute top-full left-0 w-full mt-2 bg-white dark:bg-[#1a2633] rounded-lg border border-gray-100 dark:border-gray-800 shadow-xl p-3 z-50 transition-all duration-300 origin-top ${isSearchFocused ? 'opacity-100 scale-100' : 'opacity-0 scale-95 pointer-events-none'}`}>
                    <div className="flex justify-between items-center mb-2 px-1">
                      <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">Recent Searches</span>
                      <button onClick={clearHistory} className="text-[10px] text-red-400 hover:text-red-500 font-bold hover:underline">Clear History</button>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {searchHistory.map((historyItem, idx) => (
                          <button
                            key={idx}
                            onClick={() => {
                              handleSearch(historyItem);
                              setIsSearchFocused(false);
                            }}
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gray-100 dark:bg-surface-dark border border-gray-200 dark:border-gray-700 hover:border-primary/50 text-xs font-medium text-slate-600 dark:text-slate-300 transition-colors"
                          >
                            <span className="material-symbols-outlined text-[14px] text-slate-400">history</span>
                            {historyItem}
                          </button>
                      ))}
                    </div>
                 </div>
              )}
            </div>

            {/* Category Filters */}
            <div className="bg-white dark:bg-card-dark rounded-xl p-4 shadow-md border border-gray-100 dark:border-gray-800 flex flex-col lg:flex-row gap-4 items-end justify-between">
              <div className="flex flex-col md:flex-row gap-4 w-full lg:w-auto">
                {/* Category Filter */}
                <div className="flex flex-col gap-1.5 w-full md:w-48">
                  <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest pl-1">Category</label>
                  <div className="relative">
                    <select 
                      className="w-full appearance-none rounded-lg border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-[#111a22] text-slate-900 dark:text-white focus:border-primary focus:ring-primary text-sm py-2.5 pl-4 pr-10 cursor-pointer transition-shadow hover:shadow-sm"
                      value={categoryFilter}
                      onChange={(e) => setCategoryFilter(e.target.value)}
                    >
                      <option value="all">All Categories</option>
                      <option value="professional">Professional</option>
                      <option value="course">Course</option>
                      <option value="project">Project Certification</option>
                      <option value="workshop">Workshop</option>
                    </select>
                    <span className="material-symbols-outlined absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-slate-400 text-xl">expand_more</span>
                  </div>
                </div>

                {/* Publisher Filter */}
                <div className="flex flex-col gap-1.5 w-full md:w-48">
                  <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest pl-1">Publisher</label>
                  <div className="relative">
                    <select 
                      className="w-full appearance-none rounded-lg border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-[#111a22] text-slate-900 dark:text-white focus:border-primary focus:ring-primary text-sm py-2.5 pl-4 pr-10 cursor-pointer transition-shadow hover:shadow-sm"
                      value={publisherFilter}
                      onChange={(e) => setPublisherFilter(e.target.value)}
                    >
                      <option value="all">All Publishers</option>
                      {publishers.map(pub => (
                        <option key={pub} value={pub}>{pub}</option>
                      ))}
                    </select>
                    <span className="material-symbols-outlined absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-slate-400 text-xl">expand_more</span>
                  </div>
                </div>

                {/* Year Filter */}
                <div className="flex flex-col gap-1.5 w-full md:w-32">
                  <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest pl-1">Year</label>
                  <div className="relative">
                    <select 
                      className="w-full appearance-none rounded-lg border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-[#111a22] text-slate-900 dark:text-white focus:border-primary focus:ring-primary text-sm py-2.5 pl-4 pr-10 cursor-pointer transition-shadow hover:shadow-sm"
                      value={yearFilter}
                      onChange={(e) => setYearFilter(e.target.value)}
                    >
                      <option value="all">All Years</option>
                      {years.map(year => (
                        <option key={year} value={year}>{year}</option>
                      ))}
                    </select>
                    <span className="material-symbols-outlined absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-slate-400 text-xl">expand_more</span>
                  </div>
                </div>
              </div>

              <div className="w-full lg:w-auto flex justify-end">
                <div className="flex items-center gap-2 text-xs font-black uppercase tracking-widest text-slate-500 dark:text-slate-400 bg-gray-100 dark:bg-surface-dark px-4 py-3 rounded-lg border border-gray-200 dark:border-gray-700">
                  <span className="material-symbols-outlined text-sm">filter_list</span>
                  <span>{filteredCerts.length} Items</span>
                </div>
              </div>
            </div>
          </div>

          <div className="w-full max-w-[960px] px-4 sm:px-6 lg:px-8 space-y-12 mb-20">
            {filteredCerts.length === 0 ? (
              <div className="text-center py-20 bg-gray-50 dark:bg-card-dark rounded-2xl border border-dashed border-gray-300 dark:border-gray-700">
                <span className="material-symbols-outlined text-4xl text-gray-400 mb-4">search_off</span>
                <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-2">No certificates found</h3>
                <p className="text-slate-500 dark:text-gray-400">Try adjusting your filters or search query to see more results.</p>
                <button 
                  onClick={() => { setCategoryFilter('all'); setYearFilter('all'); setPublisherFilter('all'); setSearchQuery(''); }}
                  className="mt-6 px-4 py-2 bg-primary text-slate-900 font-bold rounded-lg text-sm hover:bg-primary-dark transition-colors"
                >
                  Clear Filters
                </button>
              </div>
            ) : (
              <>
                {professionalCerts.length > 0 && (
                  <CertSection 
                    title="Professional Certifications" 
                    icon="verified" 
                    data={professionalCerts} 
                  />
                )}

                {courseCerts.length > 0 && (
                  <CertSection 
                    title="Course Certifications" 
                    icon="school" 
                    data={courseCerts} 
                  />
                )}

                {projectCerts.length > 0 && (
                  <CertSection 
                    title="Project Certifications" 
                    icon="architecture" 
                    data={projectCerts} 
                  />
                )}

                {workshopCerts.length > 0 && (
                  <CertSection 
                    title="Workshop & Seminars" 
                    icon="lightbulb" 
                    data={workshopCerts} 
                  />
                )}
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
};

// --- HELPER COMPONENTS ---

// Section with Pagination
const CertSection = ({ title, icon, data }: { title: string, icon: string, data: Certification[] }) => {
  const [page, setPage] = useState(1);
  const itemsPerPage = 4;
  const totalPages = Math.ceil(data.length / itemsPerPage);
  
  const startIndex = (page - 1) * itemsPerPage;
  const currentItems = data.slice(startIndex, startIndex + itemsPerPage);

  // Reset page when data changes (e.g., search filter applied)
  useEffect(() => {
    setPage(1);
  }, [data.length]);

  return (
    <section>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <span className="material-symbols-outlined text-primary text-2xl">{icon}</span>
          <h2 className="text-slate-900 dark:text-white text-2xl font-black">{title}</h2>
        </div>
        
        {totalPages > 1 && (
          <div className="flex items-center gap-2 self-end sm:self-auto">
            <button 
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="size-8 flex items-center justify-center rounded-full bg-gray-100 dark:bg-[#1a2633] hover:bg-gray-200 dark:hover:bg-[#2a3b4d] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              <span className="material-symbols-outlined text-sm">arrow_back</span>
            </button>
            <span className="text-xs font-bold text-slate-500 dark:text-slate-400 px-2 whitespace-nowrap">
              {page} / {totalPages}
            </span>
            <button 
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="size-8 flex items-center justify-center rounded-full bg-gray-100 dark:bg-[#1a2633] hover:bg-gray-200 dark:hover:bg-[#2a3b4d] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              <span className="material-symbols-outlined text-sm">arrow_forward</span>
            </button>
          </div>
        )}
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {currentItems.map((cert) => <CertCard key={cert.id} cert={cert} />)}
      </div>
    </section>
  );
};

// UPDATE: CertCard with Dynamic Button Logic
const CertCard: React.FC<{ cert: Certification }> = ({ cert }) => {
  // A link is considered valid if it's non-empty and not just a placeholder '#'
  const hasCredential = cert.credentialUrl 
                        && cert.credentialUrl.trim() !== '' 
                        && cert.credentialUrl !== '#';
  
  // Valid -> Show "Verify Credential" button, else "View Certificate" button
  const targetUrl = hasCredential ? cert.credentialUrl : cert.imageUrl;
  const btnText = hasCredential ? "Verify Credential" : "View Certificate";
  const btnIcon = hasCredential ? "open_in_new" : "image"; 

  return (
    <div className={`group flex flex-col bg-white dark:bg-card-dark rounded-xl shadow-sm border border-gray-100 dark:border-gray-800 transition-all duration-300 overflow-hidden ${cert.hoverColor} hover:-translate-y-1`}>
      {/* Top Image Section */}
      <div className="h-40 w-full bg-cover bg-center relative" style={{ backgroundImage: `url("${cert.imageUrl}")` }}>
        <div className="absolute inset-0 bg-black/40 group-hover:bg-black/20 transition-all"></div>
      </div>
      
      {/* Bottom Content Section */}
      <div className="p-5 flex flex-col flex-1 gap-4">
        <div className="flex flex-col gap-1">
          <h3 className="text-slate-900 dark:text-white text-lg font-black leading-tight group-hover:text-inherit transition-colors">{cert.title}</h3>
          <div className="flex items-center gap-2 text-slate-500 text-[10px] font-black uppercase tracking-widest">
             <span>{cert.issuer}</span>
             <span>•</span>
             <span>{cert.date}</span>
          </div>
          <p className="text-slate-500 dark:text-gray-400 text-xs mt-2 line-clamp-2">{cert.description}</p>
        </div>
        
        {/* Dynamic Button */}
        <a 
          href={targetUrl} 
          target="_blank" 
          rel="noopener noreferrer" 
          className="flex w-full items-center justify-center gap-2 rounded-lg h-10 px-4 bg-slate-100 dark:bg-[#233648] hover:bg-slate-200 dark:hover:bg-[#2f455a] text-slate-700 dark:text-white text-xs font-black uppercase tracking-widest transition-all"
        >
          <span>{btnText}</span>
          <span className="material-symbols-outlined text-[18px]">{btnIcon}</span>
        </a>
      </div>
    </div>
  );
};

export default Certifications;